import { Injectable, OnDestroy } from "@angular/core";
import { Subject, BehaviorSubject, fromEvent } from "rxjs";
import { takeUntil, debounceTime } from "rxjs/operators";
import { Router } from "@angular/router";
import { AuthService } from "src/app/services/auth.service";
import { ModulesDataService } from "src/app/services/data/modules-data.service";

// Menu
export interface Menu {
  headTitle1?: string;
  headTitle2?: string;
  path?: string;
  title?: string;
  icon?: string;
  type?: string;
  badgeType?: string;
  badgeValue?: string;
  active?: boolean;
  bookmark?: boolean;
  children?: Menu[];
  hasPermission?: boolean;
  translations?: { [key: string]: string };
  group?: string;
}

@Injectable({
  providedIn: "root",
})
export class NavService implements OnDestroy {
  private unsubscriber: Subject<any> = new Subject();
  public screenWidth: BehaviorSubject<number> = new BehaviorSubject(
    window.innerWidth
  );

  // Search Box
  public search: boolean = false;

  // Language
  public language: boolean = false;

  // Mega Menu
  public megaMenu: boolean = false;
  public levelMenu: boolean = false;
  public megaMenuColapse: boolean = window.innerWidth < 1199 ? true : false;

  // Collapse Sidebar
  public collapseSidebar: boolean = window.innerWidth < 991 ? true : false;

  // For Horizontal Layout Mobile
  public horizontal: boolean = window.innerWidth < 991 ? false : true;

  // Full screen
  public fullScreen: boolean = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private modulesDataService: ModulesDataService
  ) {
    this.setScreenWidth(window.innerWidth);
    fromEvent(window, "resize")
      .pipe(debounceTime(1000), takeUntil(this.unsubscriber))
      .subscribe((evt: any) => {
        this.setScreenWidth(evt.target.innerWidth);
        if (evt.target.innerWidth < 991) {
          this.collapseSidebar = true;
          this.megaMenu = false;
          this.levelMenu = false;
        }
        if (evt.target.innerWidth < 1199) {
          this.megaMenuColapse = true;
        }
      });
    if (window.innerWidth < 991) {
      // Detect Route change sidebar close
      this.router.events.subscribe((event) => {
        this.collapseSidebar = true;
        this.megaMenu = false;
        this.levelMenu = false;
      });
    }
  }

  ngOnDestroy() {
    // this.unsubscriber.next();
    this.unsubscriber.complete();
  }

  private setScreenWidth(width: number): void {
    this.screenWidth.next(width);
  }

  MENUITEMS: Menu[] = [
    {
      title: "Dashboard",
      icon: "fas fa-home", // Font Awesome icon for Dashboard
      type: "link",
      path: "/dashboard/default",
      badgeType: "light-primary",
      hasPermission: this.hasPermission("Main Dashboard"),
      active: true,
    },
    {
      title: "Groups",
      icon: "fas fa-users", // Font Awesome icon for Groups
      type: "link",
      badgeType: "light-primary",
      path: "/groups",
      hasPermission: this.isSuperAdmin(),
    },
    {
      title: "Modules",
      icon: "fas fa-folder", // Font Awesome icon for Groups
      type: "link",
      badgeType: "light-primary",
      path: "/modules",
      hasPermission: this.isSuperAdmin(),
    },
    {
      title: "Accounting",
      icon: "fas fa-money-bill-wave", // Font Awesome icon for Accounting
      type: "sub",
      badgeType: "light-primary",
      hasPermission: this.hasPermission("Accounting"),
      children: [
        {
          path: "/accounting/dashboard",
          title: "Dashboard",
          type: "link",
          hasPermission: this.hasPermission("Accounting Dashboard"),
        },
        {
          path: "/accounting/budgets",
          title: "Budgets",
          type: "link",
          hasPermission: this.hasPermission("Budgets"),
        },
      ],
    },
    {
      title: "HR Records",
      icon: "fas fa-user-friends", // Font Awesome icon for HR Records
      type: "sub",
      badgeType: "light-primary",
      hasPermission: this.hasPermission("HR Records"),
      children: [
        {
          path: "/hr/dashboard",
          title: "Dashboard",
          type: "link",
          hasPermission: this.hasPermission("HR Dashboard"),
        },
        {
          path: "/hr/recruitments/dashboard",
          title: "Recruitment/Internal mobility",
          type: "link",
          hasPermission: this.hasPermission("Recruitments"),
        },
        {
          path: "/hr/employees/dashboard",
          title: "Employees",
          type: "link",
          hasPermission: this.hasPermission("Employees"),
        },
        {
          path: "/hr/organizations",
          title: "Organization Chart",
          type: "link",
          hasPermission: this.hasPermission("Organization Chart"),
        },
        {
          path: "/hr/contracts/dashboard",
          title: "Contracts",
          type: "link",
          hasPermission: this.hasPermission("Contracts"),
        },
        {
          path: "/hr/trainings/dashboard",
          title: "Training management",
          type: "link",
          hasPermission: this.hasPermission("Training Management"),
        },
        {
          path: "/hr/assessments/dashboard",
          title: "Assessment / Evaluation",
          type: "link",
          hasPermission: this.hasPermission("Assessment/Evaluation"),
        },
      ],
    },
    {
      title: "Skill Improvement",
      icon: "fas fa-tools", // Font Awesome icon for Groups
      type: "link",
      badgeType: "light-primary",
      path: "/hr/skill-improvement",
      hasPermission: this.hasPermission("Skill Improvement"),
    },
    {
      title: "Jobs",
      icon: "fas fa-briefcase", // Font Awesome icon for Jobs
      type: "link",
      badgeType: "light-primary",
      hasPermission: this.hasPermission("Jobs"),
      path: "/hr/recruitments/jobs",
    },
    {
      title: "Performance",
      icon: "fas fa-chart-line", // Font Awesome icon for Performance
      type: "link",
      badgeType: "light-primary",
      hasPermission: this.hasPermission("Performance"),
      path: "/hr/objectives",
    },
    {
      title: "Feedback",
      icon: "fas fa-comment-dots", // Font Awesome icon for Feedback
      type: "sub",
      badgeType: "light-primary",
      hasPermission: this.hasPermission("Feedback Sessions"),
      children: [
        {
          title: "Feedback Sessions",
          type: "link",
          badgeType: "light-primary",
          hasPermission: this.hasPermission("Feedback Sessions"),
          path: "/hr/feedback-sessions",
        },
        {
          title: "Employee Satisfaction Feedback",
          type: "link",
          badgeType: "light-primary",
          hasPermission: this.hasPermission("Feedback Sessions"),
          path: "/hr/employees/feedbacks",
        },
      ],
    },
    {
      title: "Documents",
      icon: "fas fa-file-alt", // Font Awesome icon for Documents
      type: "link",
      badgeType: "light-primary",
      hasPermission: this.hasPermission("Documents"),
      path: "/documents",
    },
    {
      title: "Setup",
      icon: "fas fa-cogs", // Font Awesome icon for Setup
      type: "sub",
      badgeType: "light-primary",
      hasPermission: this.hasPermission("Setup"),
      children: [
        {
          path: "/setup/companies",
          title: "Companies",
          type: "link",
          hasPermission: this.hasPermission("Companies"),
        },
        {
          title: "HRMS",
          type: "sub",
          hasPermission: this.hasPermission("HR Records"),
          children: [
            {
              path: "/hr/recruitments/departments",
              title: "Departments",
              type: "link",
              hasPermission: this.hasPermission("HR Records"),
            },
            {
              path: "/hr/recruitments/roles",
              title: "Roles",
              type: "link",
              hasPermission: this.hasPermission("HR Records"),
            },
            {
              path: "/hr/recruitments/skills",
              title: "Skills management",
              type: "link",
              hasPermission: this.hasPermission("HR Records"),
            },
            {
              path: "/hr/recruitments/job-positions",
              title: "Job Positions",
              type: "link",
              hasPermission: this.hasPermission("HR Records"),
            },
            {
              path: "/hr/employees/job-types",
              title: "Job Types",
              type: "link",
              hasPermission: this.hasPermission("HR Records"),
            },
            {
              path: "/hr/employees/grades",
              title: "Grades",
              type: "link",
              hasPermission: this.hasPermission("HR Records"),
            },
            {
              path: "/hr/employees/seniorities",
              title: "Seniority",
              type: "link",
              hasPermission: this.hasPermission("HR Records"),
            },
            {
              path: "/hr/employees/fields",
              title: "Field of Activity",
              type: "link",
              hasPermission: this.hasPermission("HR Records"),
            },
            {
              path: "/hr/objectives",
              title: "Objectives",
              type: "link",
              hasPermission: this.hasPermission("HR Records"),
            },
          ],
        },
        {
          path: "/settings",
          title: "Settings",
          type: "link",
          hasPermission: this.hasPermission("Settings"),
        },
      ],
    },
    {
      title: "Recruitments",
      path: "/setup/companies/recruitments",
      icon: "fas fa-user-plus", // Font Awesome icon for Recruitments
      type: "link",
      badgeType: "light-primary",
      hasPermission: this.isExternalCompany(),
    },
    {
      title: "Candidates",
      path: "/setup/companies/candidates",
      icon: "fas fa-user-check", // Font Awesome icon for Candidates
      type: "link",
      badgeType: "light-primary",
      hasPermission: this.isExternalCompany(),
    },
    {
      title: "Profile",
      path: "/hr/employees/profile/" + this.employeeDetails.id,
      icon: "fas fa-id-card", // Font Awesome icon for Profile
      type: "link",
      badgeType: "light-primary",
      hasPermission: this.employeeDetails ? true : false,
    },
  ];

  // Array
  items = new BehaviorSubject<Menu[]>(this.MENUITEMS);

  hasPermission(moduleName: string): boolean {
    const module = this.modulesDataService.findModuleByName(moduleName);
    if (module) return this.authService.hasPermission(module.id);
    return false;
  }

  isExternalCompany() {
    if (this.authService.isLoggedIn()) {
      return this.authService.isExternalCompany();
    }

    return false;
  }

  isSuperAdmin(): boolean {
    if (this.authService.isLoggedIn()) {
      return this.authService.isSuperAdmin();
    }

    return false;
  }

  get employeeDetails() {
    return this.authService.employee || {};
  }
}
